// LintSummary — collapsible badge + per-dimension details for a LintReport.
// Per LLM_NATIVE_ARCHITECTURE.md §5: surfaces lint signals to the user.
//
// Shown after pipeline-complete in IngestProgress + on result-view header.
// Severity icons (lucide) + verdict colors come from CSS design tokens
// (tokens.css `--color-verdict-*`) — independent of the §3 colorForOverallState
// helper (which is dead code · DEBT-19BO+-OVERALL-STATE-DEAD-CODE). The token
// palette is the single source of truth for verdict coloring.

import { useState } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, ChevronDown, ChevronRight, Info } from 'lucide-react';
import type { LintReport, LintIssue, LintDimension, LintVerdict } from '@examforge/shared/schemas';
import type { DictionaryKey } from '@examforge/shared/i18n';
import { useT } from '../i18n/index.js';

const DIMENSION_LABEL_KEY: Record<LintDimension, DictionaryKey> = {
  mcq_distractor_balance: 'lint.kind.mcqDistractorBalance',
  difficulty_distribution: 'lint.kind.difficultyDistribution',
  cognitive_coverage: 'lint.kind.cognitiveCoverage',
  topic_coverage: 'lint.kind.topicCoverage',
  citation_trace: 'lint.kind.citationTrace',
  source_ref: 'lint.kind.sourceRef',
  shared_concepts: 'lint.kind.sharedConcepts',
  option_parity: 'lint.kind.optionParity',
};

const VERDICT_LABEL_KEY: Record<LintVerdict, DictionaryKey> = {
  high_conf: 'lint.verdictHighConf',
  low_conf: 'lint.verdictLowConf',
  unknown: 'lint.verdictUnknown',
};

// E-5 (19BM): palette now references CSS tokens (no inline hex literals).
//   See tokens.css `--color-verdict-*`. Spec §1.3 token-first principle.
const VERDICT_COLOR: Record<LintVerdict, { bg: string; fg: string; border: string }> = {
  high_conf: { bg: 'var(--color-verdict-high-bg)', fg: 'var(--color-verdict-high-fg)', border: 'var(--color-verdict-high-border)' },
  low_conf:  { bg: 'var(--color-verdict-low-bg)',  fg: 'var(--color-verdict-low-fg)',  border: 'var(--color-verdict-low-border)' },
  unknown:   { bg: 'var(--color-verdict-unknown-bg)', fg: 'var(--color-verdict-unknown-fg)', border: 'var(--color-verdict-unknown-border)' },
};

function severityIcon(s: LintIssue['severity']) {
  // E-5 (19BM · spec §3.2): inline hex → token (#b91c1c→error / #92400e→warning / #1e3a5f→primary).
  if (s === 'error') return <AlertCircle size={14} aria-hidden="true" style={{ color: 'var(--color-error)' }} />;
  if (s === 'warn') return <AlertTriangle size={14} aria-hidden="true" style={{ color: 'var(--color-warning)' }} />;
  return <Info size={14} aria-hidden="true" style={{ color: 'var(--color-primary)' }} />;
}

function VerdictBadge({ report }: { report: LintReport }) {
  const t = useT();
  const palette = VERDICT_COLOR[report.verdict];
  const Icon = report.verdict === 'high_conf' ? CheckCircle2 : report.verdict === 'low_conf' ? AlertCircle : AlertTriangle;
  return (
    <span
      className="lint-summary-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.3rem 0.65rem',
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: '0.85rem',
        fontWeight: 600,
      }}
    >
      <Icon size={14} aria-hidden="true" />
      {t(VERDICT_LABEL_KEY[report.verdict])}
      <span style={{ opacity: 0.7, fontWeight: 400 }}>
        {t('lint.severityCount', { error: report.bySeverity.error, warn: report.bySeverity.warn, info: report.bySeverity.info })}
      </span>
    </span>
  );
}

export interface LintSummaryProps {
  report: LintReport;
  /** When `defaultOpen=true` issues table is expanded on first render. */
  defaultOpen?: boolean;
}

/** Group issues by dimension; preserve insertion order within group. */
function groupIssues(issues: readonly LintIssue[]): Map<LintDimension, LintIssue[]> {
  const map = new Map<LintDimension, LintIssue[]>();
  for (const i of issues) {
    let arr = map.get(i.dimension);
    if (!arr) {
      arr = [];
      map.set(i.dimension, arr);
    }
    arr.push(i);
  }
  return map;
}

export function LintSummary({ report, defaultOpen = false }: LintSummaryProps) {
  const [open, setOpen] = useState(defaultOpen);
  const t = useT();
  const grouped = groupIssues(report.issues);

  return (
    <section className="lint-summary" aria-label={t('lint.title')}>
      <div className="lint-summary-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <VerdictBadge report={report} />
        {report.totalIssues > 0 && (
          <button
            type="button"
            className="lint-summary-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.25rem 0.5rem',
              border: '1px solid var(--color-line)',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: 'var(--color-primary)',
            }}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {open ? t('lint.toggleShow') : t('lint.toggleHide', { count: report.totalIssues })}
          </button>
        )}
      </div>

      {open && grouped.size > 0 && (
        <div className="lint-summary-body" style={{ marginTop: '0.75rem' }}>
          {[...grouped].map(([dim, items]) => (
            <details key={dim} open className="lint-summary-group" style={{ marginBottom: '0.6rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--color-primary)', fontSize: '0.9rem' }}>
                {t(DIMENSION_LABEL_KEY[dim])} <span style={{ opacity: 0.6, fontWeight: 400 }}>({items.length})</span>
              </summary>
              <ul style={{ listStyle: 'none', padding: '0.5rem 0 0 0.25rem', margin: 0 }}>
                {items.map((iss, i) => (
                  <li
                    key={`${iss.location}-${i}`}
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                      padding: '0.35rem 0',
                      borderBottom: '1px dashed rgba(0,0,0,0.06)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <span style={{ marginTop: '2px' }}>{severityIcon(iss.severity)}</span>
                    <div style={{ flex: 1 }}>
                      <div>
                        <code style={{ fontSize: '0.78rem', color: 'var(--color-ink-soft)' }}>{iss.location}</code>
                      </div>
                      <div>{iss.message}</div>
                      {iss.hint && (
                        <div style={{ opacity: 0.7, fontSize: '0.78rem', marginTop: '0.15rem' }}>
                          <strong>{t('lint.suggestion')}</strong> {iss.hint}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
